/**
 * DictionarySection — bordered table of dictionary words with per-row
 * remove (40x40 IconButton hit target). Adds happen from the
 * spellcheck flyout's "Add to dictionary" button; this view is
 * read + remove only.
 *
 * Table styling mirrors the Words to Avoid Figma (node 760:21069):
 * a single rounded-border container with internal hairline dividers
 * between rows.
 */

import React, { useEffect, useMemo, useState } from "react";
import Auth from "src/Auth";
import IconButton from "src/components/IconButton";
import { getCurrentEditor } from "src/lib/Editor";
import { cn } from "src/lib/cn";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { extractTextFromTipTap } from "../../../../../src/shared/schemas/CharacterSchema";

interface DictionaryWord {
  id: string;
  word: string;
  created_at: string;
}

export function DictionarySection() {
  const [words, setWords] = useState<DictionaryWord[]>([]);
  const [loaded, setLoaded] = useState(false);

  const charactersEntities = useStoryBibleStore((s: any) => s.charactersEntities) ?? [];
  const worldbuildingEntries = useStoryBibleStore((s: any) => s.worldbuildingEntries) ?? [];

  const characterNames = useMemo(() => {
    const names: string[] = [];
    for (const c of charactersEntities) {
      if (!c) continue;
      const name = c.displayName ?? (c.name != null ? extractTextFromTipTap(c.name) : "");
      if (name.trim()) names.push(name.trim());
    }
    return names;
  }, [charactersEntities]);

  const worldbuildingNames = useMemo(() => {
    const names: string[] = [];
    for (const wb of worldbuildingEntries) {
      if (!wb) continue;
      const name = wb.name != null ? extractTextFromTipTap(wb.name) : "";
      if (name.trim()) names.push(name.trim());
    }
    return names;
  }, [worldbuildingEntries]);

  useEffect(() => {
    let cancelled = false;
    Auth.fetchWithIdToken("/api/style-settings/dictionary/list", {})
      .then((data: any) => {
        if (!cancelled) {
          if (data?.words) setWords(data.words);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onDelete = async (id: string, word: string) => {
    const prev = words;
    setWords((rows) => rows.filter((r) => r.id !== id));
    try {
      await Auth.fetchWithIdToken("/api/style-settings/dictionary/delete", { id });
      const editor = getCurrentEditor?.();
      if (editor?.commands?.removeLocalDictionaryWord) {
        editor.commands.removeLocalDictionaryWord(word);
      }
      if (editor?.commands?.recheckGrammarFull) {
        editor.commands.recheckGrammarFull(true);
      }
    } catch {
      setWords(prev);
    }
  };

  if (!loaded) {
    return <p className="m-0 text-sm text-app-grey-1 font-inter">Loading…</p>;
  }

  const storyBibleSection = (characterNames.length > 0 || worldbuildingNames.length > 0) ? (
    <div className="flex flex-col gap-4 mt-4">
      <h4 className="m-0 font-inter font-semibold text-base text-app-grey-0">
        Character and Worldbuilding element names in your current project's Story Bible
      </h4>
      {characterNames.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm font-semibold text-app-grey-1 font-inter">Characters</p>
          <div
            className="rounded-lg border border-solid border-app-grey-3 overflow-hidden bg-app-menu-color"
            role="table"
            aria-label="Character dictionary entries"
          >
            {characterNames.map((name, index) => (
              <div
                key={name}
                role="row"
                className={cn(
                  "flex items-center gap-3 h-14 px-4",
                  index > 0 && "border-t border-solid border-app-grey-3"
                )}
              >
                <span className="flex-1 truncate text-sm text-app-grey-0 font-inter">
                  {name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {worldbuildingNames.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-sm font-semibold text-app-grey-1 font-inter">Worldbuilding</p>
          <div
            className="rounded-lg border border-solid border-app-grey-3 overflow-hidden bg-app-menu-color"
            role="table"
            aria-label="Worldbuilding dictionary entries"
          >
            {worldbuildingNames.map((name, index) => (
              <div
                key={name}
                role="row"
                className={cn(
                  "flex items-center gap-3 h-14 px-4",
                  index > 0 && "border-t border-solid border-app-grey-3"
                )}
              >
                <span className="flex-1 truncate text-sm text-app-grey-0 font-inter">
                  {name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  ) : null;

  if (words.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-app-grey-1 font-inter [text-wrap:pretty]">
          Words you add from the spellcheck popover will appear here. You can
          remove them at any time.
        </p>
        {storyBibleSection}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className="rounded-lg border border-solid border-app-grey-3 overflow-hidden bg-app-menu-color"
        role="table"
        aria-label="Dictionary words"
      >
      {words.map((row, index) => (
        <div
          key={row.id}
          role="row"
          data-dictionary-word={row.word}
          className={cn(
            "flex items-center gap-3 h-14 px-4",
            index > 0 && "border-t border-solid border-app-grey-3"
          )}
        >
          <span className="flex-1 truncate text-sm text-app-grey-0 font-inter">
            {row.word}
          </span>
          <IconButton
            size={32}
            iconSize={16}
            src="/assets/close.svg"
            smartFill
            aria-label={`Remove ${row.word}`}
            onClick={() => onDelete(row.id, row.word)}
          />
        </div>
      ))}
      </div>
      {storyBibleSection}
    </div>
  );
}
